import React from "react";
import { Linking, Platform, Pressable, Text, View } from "react-native";

import type {
  TCommunicationsCallNote,
  TCommunicationsRecording,
} from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { styles } from "../HomeConversations.styles";
import {
  recordingMatchesAudioSource,
  toVoiceNoteFromCallNote,
} from "../utils/parsers";
import ConsultNoteSummary from "./ConsultNoteSummary";

type CallRecordingsCardProps = {
  selectedRecordings: TCommunicationsRecording[];
  selectedCallNotes: TCommunicationsCallNote[];
  expandedCallNoteSummaries: Record<string, boolean>;
  reprocessingRecordingId: number | null;
  onToggleCallNoteSummary: (noteId: string) => void;
  onReprocessRecording: (recording: TCommunicationsRecording) => void;
  formatDuration: (durationSeconds?: number | null) => string;
  formatDateTime: (value?: string | null) => string;
};

const CallRecordingsCard: React.FC<CallRecordingsCardProps> = ({
  selectedRecordings,
  selectedCallNotes,
  expandedCallNoteSummaries,
  reprocessingRecordingId,
  onToggleCallNoteSummary,
  onReprocessRecording,
  formatDuration,
  formatDateTime,
}) => {
  const matchedRecordingIdByNoteId = React.useMemo(() => {
    const nextMap = new Map<number, number>();

    selectedCallNotes.forEach((note) => {
      const matchedRecording = selectedRecordings.find((recording) =>
        (note.audio_info || []).some((audioInfo) =>
          recordingMatchesAudioSource(recording, {
            s3Key: audioInfo.s3_key,
            getUrl: audioInfo.get_url,
          }),
        ),
      );
      if (matchedRecording) {
        nextMap.set(note.id, matchedRecording.id);
      }
    });

    return nextMap;
  }, [selectedCallNotes, selectedRecordings]);

  const matchedRecordingIds = React.useMemo(
    () => new Set<number>(Array.from(matchedRecordingIdByNoteId.values())),
    [matchedRecordingIdByNoteId],
  );

  const pairedRows = React.useMemo(
    () =>
      selectedCallNotes
        .map((note) => {
          const recordingId = matchedRecordingIdByNoteId.get(note.id);
          if (!recordingId) {
            return null;
          }
          const recording =
            selectedRecordings.find((item) => item.id === recordingId) || null;
          if (!recording) {
            return null;
          }
          return { note, recording };
        })
        .filter(
          (
            item,
          ): item is {
            note: TCommunicationsCallNote;
            recording: TCommunicationsRecording;
          } => !!item,
        ),
    [matchedRecordingIdByNoteId, selectedCallNotes, selectedRecordings],
  );

  const unmatchedRecordings = React.useMemo(
    () =>
      selectedRecordings.filter(
        (recording) => !matchedRecordingIds.has(recording.id),
      ),
    [matchedRecordingIds, selectedRecordings],
  );

  const unmatchedNotes = React.useMemo(
    () =>
      selectedCallNotes.filter(
        (note) => !matchedRecordingIdByNoteId.has(note.id),
      ),
    [matchedRecordingIdByNoteId, selectedCallNotes],
  );

  const hasRows =
    pairedRows.length > 0 ||
    unmatchedRecordings.length > 0 ||
    unmatchedNotes.length > 0;

  const renderRecording = (recording: TCommunicationsRecording) => (
    <View style={styles.artifactRecordingSection}>
      <Text style={styles.metaText}>
        {formatDuration(recording.duration_seconds)} |{" "}
        {formatDateTime(recording.created_at)}
      </Text>

      {recording.recording_url && Platform.OS === "web" ? (
        <View style={styles.recordingPlayerContainer}>
          {React.createElement("audio" as any, {
            controls: true,
            preload: "metadata",
            src: recording.recording_url,
            style: {
              width: "100%",
              minHeight: 44,
              borderRadius: ADMIN_TOKENS.radius.md,
            } as any,
          })}
        </View>
      ) : recording.recording_url ? (
        <Pressable
          style={[styles.callAgainButton, styles.recordingActionButton]}
          onPress={() =>
            Linking.openURL(recording.recording_url || "").catch(
              () => undefined,
            )
          }
          accessibilityLabel={`Open recording ${recording.recording_sid}`}
        >
          <Text style={styles.callAgainButtonText}>Open Recording</Text>
        </Pressable>
      ) : (
        <Text style={styles.emptyText}>Recording unavailable.</Text>
      )}

      <View style={styles.recordingActionsRow}>
        <Pressable
          style={[
            styles.callAgainButton,
            styles.recordingActionButton,
            reprocessingRecordingId === recording.id &&
              styles.callAgainButtonDisabled,
          ]}
          onPress={() => onReprocessRecording(recording)}
          disabled={reprocessingRecordingId === recording.id}
          accessibilityLabel={`Reprocess recording ${recording.recording_sid} as phone call note`}
        >
          <Text style={styles.callAgainButtonText}>
            {reprocessingRecordingId === recording.id
              ? "Reprocessing..."
              : "Reprocess Audio"}
          </Text>
        </Pressable>
      </View>
    </View>
  );

  return (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Call Recordings & Notes</Text>
      {!hasRows ? (
        <Text style={styles.emptyText}>
          No recordings or call notes are linked yet.
        </Text>
      ) : (
        <View style={styles.listContainer}>
          {pairedRows.map(({ note, recording }, noteIndex) => (
            <View key={`${note.id}-${recording.id}`} style={styles.noteRow}>
              <View style={styles.sectionStack}>
                {renderRecording(recording)}
              </View>
              <View style={styles.sectionStack}>
                <Text style={styles.callNumberText}>
                  {formatDateTime(note.created_at)}
                </Text>
                <ConsultNoteSummary
                  note={toVoiceNoteFromCallNote(note, noteIndex)}
                  expandedSummaries={expandedCallNoteSummaries}
                  onToggleSummary={onToggleCallNoteSummary}
                />
              </View>
            </View>
          ))}

          {unmatchedRecordings.map((recording) => (
            <View key={recording.id} style={styles.recordingRow}>
              {renderRecording(recording)}
            </View>
          ))}

          {unmatchedNotes.map((note, noteIndex) => (
            <View key={note.id} style={styles.noteRow}>
              <Text style={styles.callNumberText}>
                {formatDateTime(note.created_at)}
              </Text>
              <ConsultNoteSummary
                note={toVoiceNoteFromCallNote(note, noteIndex)}
                expandedSummaries={expandedCallNoteSummaries}
                onToggleSummary={onToggleCallNoteSummary}
              />
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default CallRecordingsCard;
